/** Money handling. Every amount in the system is an integer count of minor units. */

/**
 * Currencies a restaurant may be denominated in.
 *
 * One list, so the onboarding picker and the settings picker cannot drift apart.
 * A restaurant's currency is chosen once and rarely changed, but it can be
 * changed — a signup mistake is otherwise permanent.
 */
/**
 * Currency used when nothing about the device says otherwise.
 *
 * The product began in Singapore and most of its merchants are in the region;
 * a wrong guess is correctable in Settings, which is why this is a default
 * rather than a question asked twice.
 */
export const DEFAULT_CURRENCY = 'SGD';

export const CURRENCIES = [
  'SGD',
  'MYR',
  'IDR',
  'PHP',
  'THB',
  'HKD',
  'TWD',
  'JPY',
  'AUD',
  'USD',
  'EUR',
  'GBP',
  'INR',
];

/**
 * Formats minor units as a currency string.
 *
 * Falls back to a plain `<CODE> <amount>` form when `Intl` is unavailable or
 * does not recognise the currency, which is the case on some trimmed-down
 * JavaScript engines.
 *
 * @param cents Amount in minor units, for example 1250 for $12.50.
 * @param currency ISO 4217 code, for example `SGD`.
 * @param locale BCP 47 locale; defaults to the runtime locale.
 * @returns A display string such as `$12.50`.
 */
export function formatMoney(cents: number, currency: string, locale?: string): string {
  const amount = cents / 100;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

/**
 * Formats minor units as a compact number, with no currency in it.
 *
 * For a column of figures that share one currency: a value axis labelled
 * `A$0 / A$500 / A$1k` repeats the unit on every line and puts a currency symbol
 * on nothing, and the card's own description is where the unit belongs. The
 * digits are rounded to the unit that fits, the way {@link formatMoneyCompact}
 * rounds them.
 *
 * @param cents Amount in minor units.
 * @returns `842`, `1.2k`, or `1.4M` depending on magnitude.
 */
export function formatAmountCompact(cents: number): string {
  const amount = cents / 100;
  // `toFixed` always leaves the point, so the trailing zeros it pads with are
  // safe to strip: `100.00` loses `.00`, never the zero that means a hundred.
  const trim = (value: number, digits: number): string =>
    value.toFixed(digits).replace(/\.?0+$/, '');

  if (Math.abs(amount) >= 1_000_000) return `${trim(amount / 1_000_000, 1)}M`;
  if (Math.abs(amount) >= 10_000) return `${trim(amount / 1_000, 1)}k`;
  if (Math.abs(amount) >= 1_000) return `${trim(amount / 1_000, 2)}k`;
  return amount.toFixed(0);
}

/**
 * Formats minor units compactly for dashboard tiles, where column width matters
 * more than cent precision.
 *
 * @param cents Amount in minor units.
 * @param currency ISO 4217 code.
 * @returns `$842`, `$1.2k`, or `$1.4M` depending on magnitude.
 */
export function formatMoneyCompact(cents: number, currency: string): string {
  return `${currencySymbol(currency)}${formatAmountCompact(cents)}`;
}

const SYMBOLS: Record<string, string> = {
  SGD: 'S$',
  USD: '$',
  EUR: '€',
  GBP: '£',
  AUD: 'A$',
  MYR: 'RM',
  IDR: 'Rp',
  PHP: '₱',
  THB: '฿',
  HKD: 'HK$',
  JPY: '¥',
  CNY: '¥',
  INR: '₹',
};

/**
 * Best-effort currency symbol. Falls back to the code itself so an unknown
 * currency renders as `XYZ 12` rather than as a wrong symbol.
 *
 * @param currency ISO 4217 code.
 * @returns The symbol, or the code when unknown.
 */
export function currencySymbol(currency: string): string {
  return SYMBOLS[currency.toUpperCase()] ?? `${currency.toUpperCase()} `;
}

/**
 * Parses free-text price entry from the merchant menu editor.
 *
 * Accepts `12`, `12.5`, `12.50`, and a leading currency symbol. Returns null for
 * anything else, which the caller surfaces as a validation error rather than
 * silently treating as zero.
 *
 * A minus sign is rejected outright rather than stripped: `-5` becoming `5`
 * would turn a typo into a valid price, and a negative price is never a
 * meaningful menu entry.
 *
 * @param input Raw text typed by the merchant.
 * @returns Amount in minor units, or null when unparseable.
 */
export function parseMoneyInput(input: string): number | null {
  const trimmed = input.trim();
  if (trimmed.includes('-')) return null;

  const cleaned = trimmed.replace(/[^0-9.]/g, '');
  if (cleaned === '' || (cleaned.match(/\./g) ?? []).length > 1) return null;

  const value = Number(cleaned);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value * 100);
}

/**
 * Renders minor units as a plain editable string, for pre-filling a text input.
 *
 * @param cents Amount in minor units.
 * @returns `12.50`; the empty string when cents is nullish.
 */
export function toMoneyInput(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '';
  return (cents / 100).toFixed(2);
}

/**
 * Applies a percentage discount the same way the database does: floor to whole
 * minor units, so the customer is never charged a fraction of a cent.
 *
 * @param cents Base amount in minor units.
 * @param percent Discount percentage between 0 and 100.
 * @returns Discount amount in minor units.
 */
export function percentOf(cents: number, percent: number): number {
  return Math.floor((cents * percent) / 100);
}
